import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useMyEnquiries } from '@shared/api/enquiry/myEnquiries.hook';
import { Loading } from '@shared/components/Loading';
import { classNames } from '@shared/utils/joinClassNamesUtils';
import { useSearchParams } from 'react-router-dom';

import EnquiryCollapse from './EnquiryCollapse';
import { EnquiryForm, EnquiryFormProps } from './EnquiryForm';

export const ENQUIRY_HISTORY_TAB = 'history';

type EnquiryPanelProps = Pick<EnquiryFormProps, 'type' | 'showTitle' | 'className'>;

/**
 * "문의" 한 화면 — [문의하기 | 내 문의내역] 탭.
 * - 탭은 `?tab=history`로 전환 (딥링크·뒤로가기 유지, 옛 /my-enquiries는 여기로 리다이렉트)
 * - 접수 성공 → 내역 탭으로 넘어가 방금 건이 맨 위에 "접수"로 보인다 (홈으로 튕기지 않음)
 * - 내역 탭을 열면 안 읽은 답변에 "새 답변"을 달고 읽음 처리 (메뉴 배지도 같이 꺼진다)
 * - 내역을 조회할 수 없는 계정(비로그인·백엔드 미허용 역할)은 폼만 보이고,
 *   접수 후엔 자리에서 완료 카드를 띄운다
 */
export const EnquiryPanel: FC<EnquiryPanelProps> = ({
  type,
  showTitle = true,
  className = 'p-4',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canRead, status, list, unreadIds, unreadCount, markSeen } = useMyEnquiries();

  const isHistory = canRead && searchParams.get('tab') === ENQUIRY_HISTORY_TAB;

  const goTab = useCallback(
    (history: boolean) => {
      const next = new URLSearchParams(searchParams);
      if (history) next.set('tab', ENQUIRY_HISTORY_TAB);
      else next.delete('tab');
      setSearchParams(next, { replace: true });
      window.scrollTo({ top: 0 });
    },
    [searchParams, setSearchParams],
  );

  //* 내역 탭에 들어온 시점의 안 읽은 답변을 스냅샷으로 들고 "새 답변"을 표시한 뒤 읽음 처리.
  //* 읽음 처리로 unreadIds가 비어도 표시는 탭을 떠날 때까지 유지된다.
  const [newIds, setNewIds] = useState<Set<string>>(() => new Set());
  const unreadIdsRef = useRef(unreadIds);
  unreadIdsRef.current = unreadIds;
  const markSeenRef = useRef(markSeen);
  markSeenRef.current = markSeen;
  const seenThisVisitRef = useRef(false);
  useEffect(() => {
    if (!isHistory) {
      seenThisVisitRef.current = false;
      return;
    }
    if (status !== 'success' || seenThisVisitRef.current) return;
    seenThisVisitRef.current = true;
    setNewIds(new Set(unreadIdsRef.current));
    if (unreadIdsRef.current.size > 0) markSeenRef.current();
  }, [isHistory, status]);

  //* 내역 조회가 안 되는 계정의 접수 완료 상태
  const [submittedWithoutHistory, setSubmittedWithoutHistory] = useState(false);
  const onSuccess = useCallback(() => {
    if (canRead) goTab(true);
    else {
      setSubmittedWithoutHistory(true);
      window.scrollTo({ top: 0 });
    }
  }, [canRead, goTab]);

  const tabClass = (active: boolean) =>
    classNames(
      'flex-1 border-b-2 py-3 text-center text-sm font-bold transition-colors',
      active
        ? 'border-gray-900 text-gray-900'
        : 'border-transparent text-gray-400 hover:text-gray-700',
    );

  if (!canRead) {
    if (submittedWithoutHistory) {
      return (
        <div className={className}>
          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 text-center">
            <p className="text-lg font-bold">문의가 접수됐어요</p>
            <p className="text-sm text-gray-600">
              남겨주신 연락처로 답변드리겠습니다.
            </p>
            <button
              type="button"
              className="button-rectangle bg-gray-900 px-6 py-3 text-sm text-white"
              onClick={() => setSubmittedWithoutHistory(false)}
            >
              새 문의 남기기
            </button>
          </div>
        </div>
      );
    }
    return (
      <EnquiryForm
        type={type}
        showTitle={showTitle}
        className={className}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <div>
      {showTitle && <h1 className="px-4 pt-8 pb-4 text-center text-xl font-bold">1:1 문의</h1>}
      <div className="flex px-4">
        <button type="button" className={tabClass(!isHistory)} onClick={() => goTab(false)}>
          문의하기
        </button>
        <button type="button" className={tabClass(isHistory)} onClick={() => goTab(true)}>
          <span className="inline-flex items-center gap-1.5">
            내 문의내역
            {unreadCount > 0 && (
              <span
                aria-label={`새 답변 ${unreadCount}건`}
                className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white"
              >
                {unreadCount}
              </span>
            )}
          </span>
        </button>
      </div>

      {isHistory ? (
        <div className="space-y-3 px-4 py-4 text-sm">
          {status !== 'success' ? (
            <div className="center-box py-8">
              <Loading />
            </div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center gap-5 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7.5 8.25h9m-9 3.75h5.25M4.5 4.5h15A1.5 1.5 0 0 1 21 6v9a1.5 1.5 0 0 1-1.5 1.5H12l-4.5 3.75V16.5h-3A1.5 1.5 0 0 1 3 15V6a1.5 1.5 0 0 1 1.5-1.5Z"
                  />
                </svg>
              </div>
              <div className="space-y-1.5">
                <p className="text-base font-bold text-gray-800">아직 남긴 문의가 없어요</p>
                <p className="text-sm leading-6 text-gray-500">
                  궁금한 점을 남기시면 답변을 여기서 확인할 수 있어요.
                </p>
              </div>
              <button
                type="button"
                className="button-rectangle bg-gray-900 px-8 py-3 text-sm font-bold text-white"
                onClick={() => goTab(false)}
              >
                첫 문의 남기기
              </button>
            </div>
          ) : (
            list.map((enquiry) => (
              //* 콘솔은 페이지 배경이 흰색이라 theme-bg만으로는 항목이 배경에 묻힌다 — 테두리·그림자로 카드를 세운다
              <div
                key={enquiry._id}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
              >
                <EnquiryCollapse enquiry={enquiry} isNew={newIds.has(enquiry._id)} />
              </div>
            ))
          )}
          {list.length > 0 && (
            <div className="center-box">
              <p className="py-2 text-xs text-gray-500">최근 1년간 문의 내역만 조회 가능합니다.</p>
            </div>
          )}
        </div>
      ) : (
        //* 제목은 탭 위에 이미 그렸으므로 폼 안의 제목은 끈다
        <EnquiryForm type={type} showTitle={false} className={className} onSuccess={onSuccess} />
      )}
    </div>
  );
};
